import { useState } from 'react';
import { toast } from 'sonner';
import { P, usePermissions } from '@/features/permissions';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { FaxProcessingDetails } from '@/features/assistant/FaxProcessingDetails';
import { SendToReferralReview } from '@/features/agent-proposals';
import { DocumentClassificationPanel } from '@/features/document-review/DocumentClassificationPanel';
import { useLabSourceRevision } from '@/features/labs/api';
import { LabReview } from '@/features/labs/LabReview';
import { Download, FileText, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { formatCalendarDate } from '@/lib/utils/date';
import { DocumentTagPicker } from './DocumentTagPicker';
import { DocumentPreview } from './DocumentPreview';
import { useDeleteUploadedDocument, useDocumentDownloadUrl, useUploadedDocument } from '../api/uploaded-documents-api';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';

interface UploadedDocumentInboxSheetProps {
  documentId: string | undefined;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  revisionId?: string;
  pageNumber?: number;
}

/**
 * Read-only-ish viewer for an Inbox uploaded document.
 *
 * The full chart-bound `UploadedDocumentSheet` requires a `patientId`
 * so it can render encounter linkage and inline metadata edits. While
 * a document still lives in the Inbox it has no patient, so we render
 * a slimmed-down view that exposes: an inline preview, file metadata, tags,
 * and a download button. After the user assigns the doc to a chart from the
 * Inbox row, the full sheet is available on the patient timeline.
 */
export function UploadedDocumentInboxSheet({
  open,
  documentId,
  onOpenChange,
  revisionId,
  pageNumber,
}: UploadedDocumentInboxSheetProps) {
  const { data: document } = useUploadedDocument(open ? documentId : undefined);
  const { can } = usePermissions();
  const remove = useDeleteUploadedDocument();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const downloadUrl = useDocumentDownloadUrl(documentId);
  const original = useLabSourceRevision(open ? documentId : undefined, revisionId);
  const revision = revisionId ? original.data : document?.current_revision;

  const handleDownload = async () => {
    if (revisionId) {
      if (revision?.file_url) window.open(revision.file_url, '_blank', 'noopener,noreferrer');
      return;
    }
    const result = await downloadUrl.refetch();
    const url = result.data?.url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // Hide the default close — we render our own in the header.
        className="flex h-full w-full flex-col overflow-hidden p-6 sm:max-w-5xl [&>[data-slot=sheet-default-close]]:hidden">
        <SheetHeader className="p-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <SheetTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 shrink-0" />
                <span className="truncate">{document?.name ?? 'Uploaded document'}</span>
              </SheetTitle>
              <SheetDescription>
                {document ? `${document.document_type} — ${formatCalendarDate(document.document_date)}` : 'Loading…'}
              </SheetDescription>
            </div>
            <SheetClose asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        <AlertDialog open={confirmDelete} onOpenChange={(value) => { if (!remove.isPending) setConfirmDelete(value); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {document?.name || 'this document'}?</AlertDialogTitle>
              <AlertDialogDescription>Remove this document from the Inbox and patient document lists. Confirm only if this is spam or an unwanted document.</AlertDialogDescription>
            </AlertDialogHeader>
            {deleteError && <p role="alert">{deleteError}</p>}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
              <Button variant="destructive" disabled={remove.isPending} onClick={async () => {
                if (!documentId) return;
                setDeleteError('');
                try { await remove.mutateAsync(documentId); setConfirmDelete(false); onOpenChange(false); toast.success('Document deleted'); }
                catch { setDeleteError('Could not delete this document. Check your access and try again.'); }
              }}>{remove.isPending ? 'Deleting…' : 'Delete document'}</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {!revisionId && document && can(P.DELETE_DOCUMENT) && <Button variant="outline" className="self-start mt-4" onClick={() => { setDeleteError(''); setConfirmDelete(true); }}>Delete document</Button>}
        <div className="mt-4 min-h-0 flex-1 overflow-y-auto pb-12 flex flex-col gap-4">
          {!revisionId && document?.source?.provider === 'srfax' && <FaxProcessingDetails documentId={document.id} />}
          {!revisionId && document?.current_revision && (
            <SendToReferralReview
              key={document.current_revision.id}
              revisionId={document.current_revision.id}
              ready={document.current_revision.extraction_status === 'extracted'}
            />
          )}
          {revisionId && original.isError && <p role="alert">The original source revision is unavailable.</p>}
          {revision && (
            <div className="rounded-md border bg-card p-3">
              <p className="text-sm font-medium truncate">{revision.file_name}</p>
              <p className="text-xs text-muted-foreground">
                {revision.content_type} · revision {revision.revision_number}
              </p>
              <Button
                size="sm"
                className="mt-2"
                variant="outline"
                disabled={downloadUrl.isFetching}
                onClick={handleDownload}>
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </Button>
            </div>
          )}

          {revision && (
            <DocumentPreview
              fileUrl={pageNumber ? `${revision.file_url}#page=${pageNumber}` : revision.file_url}
              fileName={revision.file_name}
              contentType={revision.content_type}
            />
          )}

          {document && (!revisionId || revisionId === document.current_revision?.id) && (
            <LabReview
              documentId={document.id}
              patientId={document.patient_id}
              primaryStatus={document.current_revision?.extraction_status}
              revisionId={document.current_revision?.id}
            />
          )}

          <Separator />

          {document && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Tags</p>
              <DocumentTagPicker kind="uploaded" documentId={document.id} />
              <DocumentClassificationPanel key={document.id} kind="uploaded" documentId={document.id} />
            </div>
          )}

          {document?.notes && (
            <div className="space-y-1">
              <p className="text-sm font-medium">Notes</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{document.notes}</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
